// ** React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// ** MUI Imports
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography
} from '@mui/material'

// ** Third Party Imports
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

// ** Component Imports
import ControlledTextfield from '../../components/controlled-textfield'
import CustomDateTimePicker from '../../components/date-time-picker'
import CustomDialog from '../../components/dialog'
import Icon from '../../components/icon'
import OverlayLoading from '../../components/overlay-loading'
import CustomTextField from '../../components/textfield'
import VirtualizedAutocomplete from '../../components/virtualized-autocomplete'
import FieldsetContainer from '../contest/edit/components/FieldsetContainer'

// ** API Imports
import { createCoupon, updateCoupon } from '../../axios/api/developer'

// ** Utils Imports
import { showErrorToast } from '../../utils/utils'
import { couponSchema } from '../../utils/validations'

const initialValues = {
  code: '',
  description: '',
  activateNow: true,
  startTime: null,
  expirationDate: null,
  isGlobal: true,
  tenantId: '',
  maxDiscountAmountUsd: '',
  maxDiscountAmountInr: '',
  isOneTimeUse: false,
  rates: [
    {
      discountRate: '',
      inrBasePrice: '',
      inrMaxPrice: '',
      usdBasePrice: '',
      usdMaxPrice: ''
    }
  ]
}

const AddUpdateCouponDialog = ({
  open = false,
  config = null,
  isEdit = false,
  onClose = () => {},
  handleRefresh = () => {},
  tenants = []
}) => {
  // states
  const [loading, setLoading] = useState(false)

  // form hooks
  const {
    handleSubmit,
    control,
    reset,
    trigger,
    watch,
    clearErrors,
    setValue,
    formState: { errors, isSubmitted, touchedFields }
  } = useForm({
    defaultValues: initialValues,
    resolver: yupResolver(couponSchema),
    mode: 'onBlur'
  })

  const {
    fields: rateFields,
    append,
    remove,
    replace
  } = useFieldArray({ control, name: 'rates' })

  // watched values
  const watchIsGlobal = watch('isGlobal')
  const watchActivateNow = watch('activateNow')
  const watchStartTime = watch('startTime')
  // variables
  const expirationMinDate = useMemo(() => {
    return watchStartTime
      ? new Date(new Date(watchStartTime).getTime() + 60 * 1000)
      : undefined
  }, [watchStartTime])

  const showErrors = useMemo(() => {
    return isSubmitted || Object.keys(touchedFields || {}).length > 0
  }, [isSubmitted, touchedFields])

  const isLocked = useMemo(() => {
    return isEdit && config?.status?.toUpperCase() !== 'INACTIVE'
  }, [isEdit, config?.status])

  // transform functions

  const apiToForm = useCallback((data) => {
    if (!data) return initialValues

    const formVal = {
      code: data?.code ?? '',
      description: data?.description ?? '',
      activateNow: data?.activateNow ?? true,
      startTime: data?.startTime ?? null,
      expirationDate: data?.expirationDate ?? null,
      isGlobal: data?.isGlobal ?? true,
      tenantId: data?.tenantId ?? '',
      maxDiscountAmountUsd: data?.maxDiscountAmountUsd ?? '',
      maxDiscountAmountInr: data?.maxDiscountAmountInr ?? '',
      isOneTimeUse: data?.isOneTimeUse ?? false,
      rates: []
    }

    const inr = data?.discountRates?.filter((x) => x.currency === 'INR')
    const usd = data?.discountRates?.filter((x) => x.currency === 'USD')

    // sort by basePrice
    inr.sort((a, b) => a.basePrice - b.basePrice)
    usd.sort((a, b) => a.basePrice - b.basePrice)

    const count = Math.max(inr.length, usd.length)

    for (let i = 0; i < count; i++) {
      formVal.rates.push({
        discountRate: inr[i]?.discountRate ?? usd[i]?.discountRate ?? '',
        inrBasePrice: inr[i]?.basePrice ?? '',
        inrMaxPrice: inr[i]?.maxPrice ?? '',
        usdBasePrice: usd[i]?.basePrice ?? '',
        usdMaxPrice: usd[i]?.maxPrice ?? ''
      })
    }

    return formVal
  }, [])

  // handlers
  const handleClose = useCallback(() => {
    reset(initialValues)
    clearErrors()
    onClose()
  }, [onClose, reset, clearErrors])

  const constructPayload = useCallback(
    (data) => {
      const rates = data?.rates || []
      const discountRates = []

      rates.forEach((row) => {
        const rate = Number(row.discountRate)

        discountRates.push({
          currency: 'INR',
          basePrice:
            row?.inrBasePrice === '' ? null : Number(row?.inrBasePrice),
          maxPrice: row?.inrMaxPrice === '' ? null : Number(row?.inrMaxPrice),
          discountRate: rate
        })

        discountRates.push({
          currency: 'USD',
          basePrice:
            row?.usdBasePrice === '' ? null : Number(row?.usdBasePrice),
          maxPrice: row?.usdMaxPrice === '' ? null : Number(row?.usdMaxPrice),
          discountRate: rate
        })
      })

      const payload = {
        code: data?.code,
        description: data?.description,
        activateNow: Boolean(data?.activateNow),
        startTime:
          data?.activateNow || !data?.startTime
            ? null
            : new Date(data.startTime).toISOString(),
        expirationDate: data?.expirationDate
          ? new Date(data.expirationDate).toISOString()
          : null,
        isGlobal: Boolean(data?.isGlobal),
        tenantId: data?.isGlobal ? null : data?.tenantId || null,
        maxDiscountAmountUsd:
          data?.maxDiscountAmountUsd === '' ? null : data?.maxDiscountAmountUsd,
        maxDiscountAmountInr:
          data?.maxDiscountAmountInr === '' ? null : data?.maxDiscountAmountInr,
        isOneTimeUse: Boolean(data?.isOneTimeUse),
        discountRates: discountRates
      }

      // include id when updating existing coupon
      if (isEdit) {
        payload.id = config?.id || data?.id || null
      }

      return payload
    },
    [isEdit, config?.id]
  )

  const onSubmit = useCallback(
    async (data) => {
      setLoading(true)
      const payload = constructPayload(data)
      const response = isEdit
        ? await updateCoupon(payload)
        : await createCoupon(payload)

      if (response?.isError) {
        showErrorToast(response)
        setLoading(false)
        return
      } else {
        toast.success(isEdit ? 'Coupon updated successfully' : 'Coupon created')
        setLoading(false)
        handleRefresh()
        handleClose()
      }
    },
    [constructPayload, handleClose, handleRefresh, isEdit]
  )

  const handleActiveNowChange = useCallback(
    (e, onChange) => {
      onChange(e.target.checked)
      if (e.target.checked) {
        setValue('startTime', null)
        clearErrors('startTime')
        clearErrors('expirationDate')
      }
    },
    [clearErrors, setValue]
  )

  const handleGlobalChange = useCallback(
    (e, onChange) => {
      onChange(e.target.checked)
      if (e.target.checked) {
        setValue('tenantId', null)
        clearErrors('tenantId')
      }
    },
    [clearErrors, setValue]
  )

  // Effects

  // set form values on edit or clone
  useEffect(() => {
    if (config) {
      const formConfig = apiToForm(config)
      if (formConfig?.activateNow) formConfig.startTime = null
      reset(formConfig)
      replace(
        formConfig?.rates?.length ? formConfig.rates : initialValues.rates
      )
    } else {
      reset(initialValues)
      replace(initialValues.rates)
    }
  }, [isEdit, config, reset, replace, apiToForm])

  useEffect(() => {
    return () => {
      reset(initialValues)
      replace(initialValues?.rates)
      clearErrors()
    }
  }, [reset, replace, clearErrors])

  return (
    <>
      {loading && <OverlayLoading />}
      <CustomDialog
        open={open}
        title={`${isEdit ? 'Update Coupon' : 'Create New Coupon'}`}
        outsideClikClose={false}
        handleClose={handleClose}
        dialogPaperStyle={{
          width: '95%',
          maxWidth: '600px',
          '& .MuiDialogContent-root': { padding: '0 !important' }
        }}
      >
        <Box className="coupon-form">
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box className="coupon-form-container" sx={{ p: 4 }}>
              <Grid container columnSpacing={4}>
                <Grid item xs={12} md={12}>
                  <FieldsetContainer label={'Coupon Details'}>
                    <Grid container columnSpacing={2} rowGap={2}>
                      <Grid item xs={12}>
                        <Controller
                          name="code"
                          control={control}
                          render={({ field }) => (
                            <CustomTextField
                              {...field}
                              label={'Code'}
                              placeholder="WELCOME10, SUMMER20, etc..."
                              fullWidth
                              required
                              disabled={isLocked}
                              error={Boolean(errors?.code)}
                              helperText={errors?.code?.message}
                              onChange={(e) => {
                                const val = e?.target?.value?.trim()
                                const upper = String(val || '').toUpperCase()
                                field.onChange(upper)
                              }}
                              onBlur={() => {
                                field.onBlur()
                                field.onChange(
                                  String(field.value || '').toUpperCase()
                                )
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <ControlledTextfield
                          control={control}
                          name={'description'}
                          required
                          label={'Description'}
                          placeholder="Describe coupon..."
                          error={Boolean(errors?.description)}
                          helperText={errors?.description?.message}
                          fullWidth
                          multiline
                          minRows={4}
                          maxRows={6}
                          disabled={isLocked}
                        />
                      </Grid>
                    </Grid>
                  </FieldsetContainer>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FieldsetContainer label={'Usage Configuration'}>
                    <Controller
                      name="isOneTimeUse"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(value)}
                              disabled={isLocked}
                              onChange={(e) => onChange(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              One Time Use
                            </Typography>
                          }
                        />
                      )}
                    />
                    <br />
                    <Controller
                      name="isGlobal"
                      control={control}
                      render={({ field: { value, onChange } }) => (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={Boolean(value)}
                              disabled={isLocked}
                              onChange={(e) => handleGlobalChange(e, onChange)}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Global Access (available to all tenants)
                            </Typography>
                          }
                        />
                      )}
                    />
                    {!watchIsGlobal && (
                      <VirtualizedAutocomplete
                        name="tenantId"
                        control={control}
                        label="Select Tenant"
                        required
                        options={tenants || []}
                        error={Boolean(errors?.tenantId)}
                        helperText={errors?.tenantId?.message}
                        needVirtualization={true}
                        autoCompleteProps={{
                          disabled: watchIsGlobal || isLocked,
                          getOptionLabel: (option) => option?.name || '',
                          isOptionEqualToValue: (opt, val) =>
                            opt?.id === val?.id,
                          value:
                            tenants.find((t) => t.id === watch('tenantId')) ||
                            null,
                          onChange: (_, selected) => {
                            setValue('tenantId', selected?.id || '')
                          }
                        }}
                      />
                    )}
                  </FieldsetContainer>
                </Grid>

                <Grid item xs={12} md={12}>
                  <FieldsetContainer label={'Activation'}>
                    <Grid container columnSpacing={4}>
                      <Grid item xs={12}>
                        <Controller
                          name="activateNow"
                          control={control}
                          render={({ field: { value, onChange } }) => (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(value)}
                                  disabled={isLocked}
                                  onChange={(e) =>
                                    handleActiveNowChange(e, onChange)
                                  }
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  Activate Now
                                </Typography>
                              }
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="startTime"
                          control={control}
                          render={({ field }) => (
                            <CustomDateTimePicker
                              {...field}
                              required={!watchActivateNow}
                              disabled={watchActivateNow || isLocked}
                              error={Boolean(errors?.startTime)}
                              {...(errors?.startTime && {
                                helperText: errors.startTime?.message
                              })}
                              allowPreviousDates={false}
                              label={'Start Date'}
                              usedInHookForm={false}
                              onChange={(date) => {
                                field.onChange(date)
                                setTimeout(() => {
                                  trigger('expirationDate')
                                })
                              }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <CustomDateTimePicker
                          name="expirationDate"
                          control={control}
                          minDate={expirationMinDate}
                          allowPreviousDates={false}
                          error={Boolean(errors?.expirationDate)}
                          {...(errors?.expirationDate && {
                            helperText: errors.expirationDate?.message
                          })}
                          label={'Expiration Date'}
                        />
                      </Grid>
                    </Grid>
                  </FieldsetContainer>
                </Grid>

                <Grid item xs={12}>
                  <FieldsetContainer label={'Discount Configuration'}>
                    <Grid container rowGap={4}>
                      <Grid item container columnSpacing={4}>
                        <Grid item xs={12} mb={2}>
                          <Typography className="section-title">
                            Maximum Discount
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ControlledTextfield
                            control={control}
                            name={'maxDiscountAmountInr'}
                            label={'Amount (INR)'}
                            placeholder="e.g. 1000"
                            error={Boolean(errors?.maxDiscountAmountInr)}
                            helperText={errors?.maxDiscountAmountInr?.message}
                            fullWidth
                            disabled={isLocked}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <ControlledTextfield
                            control={control}
                            name={'maxDiscountAmountUsd'}
                            label={'Amount (USD)'}
                            placeholder="e.g. 20"
                            error={Boolean(errors?.maxDiscountAmountUsd)}
                            helperText={errors?.maxDiscountAmountUsd?.message}
                            fullWidth
                            disabled={isLocked}
                          />
                        </Grid>
                      </Grid>

                      {rateFields?.map((row, index) => {
                        const checkErrors = errors?.rates?.[index] || null
                        return (
                          <Grid item xs={12} key={row?.id || index}>
                            {index === 0 && (
                              <Typography
                                className="section-title"
                                mt={4}
                                mb={2}
                              >
                                Rates (INR + USD per rate)
                              </Typography>
                            )}
                            <FieldsetContainer
                              label={`Rate ${index + 1}`}
                              mb={0}
                            >
                              <Grid container columnSpacing={4}>
                                <Grid item alignContent={'start'} xs={3}>
                                  <ControlledTextfield
                                    control={control}
                                    required
                                    name={`rates.${index}.discountRate`}
                                    label="Discount %"
                                    error={
                                      showErrors && !!checkErrors?.discountRate
                                    }
                                    {...(showErrors &&
                                      checkErrors?.discountRate && {
                                        helperText:
                                          checkErrors?.discountRate?.message
                                      })}
                                    disabled={isLocked}
                                  />
                                </Grid>

                                <Grid item container spacing={4} xs={9}>
                                  <Grid item xs={12} md={6}>
                                    <ControlledTextfield
                                      control={control}
                                      name={`rates.${index}.inrBasePrice`}
                                      required
                                      label="INR Base"
                                      error={
                                        showErrors &&
                                        !!checkErrors?.inrBasePrice
                                      }
                                      {...(showErrors &&
                                        checkErrors?.inrBasePrice && {
                                          helperText:
                                            checkErrors?.inrBasePrice?.message
                                        })}
                                      disabled={isLocked}
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <ControlledTextfield
                                      control={control}
                                      name={`rates.${index}.inrMaxPrice`}
                                      label="INR Max"
                                      error={
                                        showErrors && !!checkErrors?.inrMaxPrice
                                      }
                                      {...(showErrors &&
                                        checkErrors?.inrMaxPrice && {
                                          helperText:
                                            checkErrors?.inrMaxPrice?.message
                                        })}
                                      disabled={isLocked}
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <ControlledTextfield
                                      control={control}
                                      name={`rates.${index}.usdBasePrice`}
                                      required
                                      label="USD Base"
                                      error={
                                        showErrors &&
                                        !!checkErrors?.usdBasePrice
                                      }
                                      {...(showErrors &&
                                        checkErrors?.usdBasePrice && {
                                          helperText:
                                            checkErrors?.usdBasePrice?.message
                                        })}
                                      disabled={isLocked}
                                    />
                                  </Grid>

                                  <Grid item xs={12} md={6}>
                                    <ControlledTextfield
                                      control={control}
                                      name={`rates.${index}.usdMaxPrice`}
                                      label="USD Max"
                                      error={
                                        showErrors && !!checkErrors?.usdMaxPrice
                                      }
                                      {...(showErrors &&
                                        checkErrors?.usdMaxPrice && {
                                          helperText:
                                            checkErrors?.usdMaxPrice?.message
                                        })}
                                      disabled={isLocked}
                                    />
                                  </Grid>
                                </Grid>
                              </Grid>

                              {!isLocked && rateFields.length > 1 && (
                                <Box className="delete-icon">
                                  <Icon
                                    icon="icon-park-outline:delete-five"
                                    onClick={() => {
                                      if (rateFields.length > 1) remove(index)
                                      else replace(initialValues?.rates)
                                    }}
                                  />
                                </Box>
                              )}
                            </FieldsetContainer>
                          </Grid>
                        )
                      })}

                      {!isLocked && (
                        <Grid item xs={12}>
                          <Button
                            startIcon={<Icon icon="ic:baseline-add" />}
                            fullWidth
                            variant="outlined"
                            disabled={isLocked}
                            onClick={() =>
                              append({
                                discountRate: '',
                                inrBasePrice: '',
                                inrMaxPrice: '',
                                usdBasePrice: '',
                                usdMaxPrice: ''
                              })
                            }
                          >
                            New Rate
                          </Button>
                        </Grid>
                      )}

                      {errors?.rates && showErrors && (
                        <Grid item xs={12}>
                          <FormHelperText error>
                            {errors.rates?.message}
                          </FormHelperText>
                        </Grid>
                      )}
                    </Grid>
                  </FieldsetContainer>
                </Grid>
              </Grid>
            </Box>

            {/* ===== FOOTER ===== */}
            <Box className="coupon-form-footer">
              <Box className="coupon-form-footer-actions"></Box>
              <Box className="coupon-form-footer-buttons">
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained">
                  {isEdit ? 'Update Coupon' : 'Create Coupon'}
                </Button>
              </Box>
            </Box>
          </form>
        </Box>
      </CustomDialog>
    </>
  )
}

export default memo(AddUpdateCouponDialog)
